import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, Progress, Radio, Spin } from "antd";
import { CheckCircleFilled, WhatsAppOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BASE_URL } from "../../Constants";

const SEND_CHUNK = 25; // the server sends one guest at a time, so keep each request short

/**
 * "Send invitations" popup for wedding events. Asks who should receive the WhatsApp invitation:
 * everyone who has not been sent one yet, or only guests whose invitation failed, or let the host
 * pick guests in the list. Sends in small batches with a progress bar and a result summary.
 */
const SendInvitationsModal = ({
  open,
  onClose,
  onDone,
  onChooseGuests,
  eventId,
  invitedBy,
  hasWhatsAppSupport,
  primaryColor,
  isMobile,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState("choose"); // choose | confirm | sending | done
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [choice, setChoice] = useState("notSent"); // notSent | failed | pick
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState({ success: 0, failed: 0, failures: [] });
  const [error, setError] = useState("");

  // Fresh numbers every time the popup opens.
  useEffect(() => {
    if (!open) return;
    setStep("choose");
    setResult({ success: 0, failed: 0, failures: [] });
    setError("");
    setSummary(null);
    setLoading(true);
    axios
      .get(`${BASE_URL}/EventWallet/invitation-summary/${eventId}`)
      .then(({ data }) => {
        setSummary(data);
        // Start on the first option that has guests in it.
        setChoice(data.notSent > 0 ? "notSent" : data.failed > 0 ? "failed" : "pick");
      })
      .catch(() => setError(t("eventRegistrations.sendInvites.loadFailed")))
      .finally(() => setLoading(false));
  }, [open, eventId]);

  const targetIds = choice === "failed" ? summary?.failedIds || [] : summary?.notSentIds || [];

  const runSend = async () => {
    const ids = [...targetIds];
    setStep("sending");
    setProgress({ done: 0, total: ids.length });
    const totals = { success: 0, failed: 0, failures: [] };

    for (let i = 0; i < ids.length; i += SEND_CHUNK) {
      const chunk = ids.slice(i, i + SEND_CHUNK);
      try {
        const { data } = await axios.post(`${BASE_URL}/EventWallet/send-whatsapp/bulk`, {
          eventId,
          registrationIds: chunk,
          invitedBy,
        });
        totals.success += data?.successCount ?? 0;
        totals.failed += data?.failedCount ?? 0;
        totals.failures.push(...(data?.failures || []));
      } catch (err) {
        totals.failed += chunk.length;
        totals.failures.push(err.response?.data?.message || t("eventRegistrations.sendInvites.batchFailed"));
      }
      setProgress({ done: Math.min(i + SEND_CHUNK, ids.length), total: ids.length });
    }

    setResult(totals);
    setStep("done");
  };

  const handleContinue = () => {
    if (choice === "pick") {
      onClose();
      onChooseGuests?.();
      return;
    }
    setStep("confirm");
  };

  const handleClose = () => {
    const sentSomething = step === "done" && result.success + result.failed > 0;
    onClose();
    if (sentSomething) onDone?.();
  };

  const optionCard = (value, title, description, disabled) => (
    <Radio.Button
      value={value}
      disabled={disabled}
      style={{
        display: "block",
        height: "auto",
        padding: "12px 14px",
        marginBottom: 10,
        borderRadius: 10,
        lineHeight: 1.5,
        whiteSpace: "normal",
      }}
    >
      <div style={{ fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 12, color: "#8c8c8c", fontWeight: 400 }}>{description}</div>
    </Radio.Button>
  );

  const chooseScreen = (
    <div>
      {!hasWhatsAppSupport && (
        <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={t("eventRegistrations.sendInvites.noWhatsApp")} />
      )}
      {error && <Alert style={{ marginBottom: 12 }} type="error" showIcon message={error} />}

      {loading || !summary ? (
        <div style={{ textAlign: "center", padding: 24 }}>{error ? null : <Spin />}</div>
      ) : (
        <>
          <div style={{ marginBottom: 12, fontWeight: 500 }}>{t("eventRegistrations.sendInvites.question")}</div>
          <Radio.Group value={choice} onChange={(e) => setChoice(e.target.value)} style={{ width: "100%" }}>
            {optionCard(
              "notSent",
              t("eventRegistrations.sendInvites.optNotSent"),
              t("eventRegistrations.sendInvites.guestCount", { count: summary.notSent }),
              summary.notSent === 0
            )}
            {summary.failed > 0 &&
              optionCard(
                "failed",
                t("eventRegistrations.sendInvites.optFailed"),
                t("eventRegistrations.sendInvites.guestCount", { count: summary.failed }),
                false
              )}
            {optionCard(
              "pick",
              t("eventRegistrations.sendInvites.optPick"),
              t("eventRegistrations.sendInvites.optPickDesc"),
              false
            )}
          </Radio.Group>

          {summary.notSent === 0 && summary.failed === 0 && (
            <div style={{ fontSize: 13, color: "#52c41a", marginTop: 4 }}>
              {t("eventRegistrations.sendInvites.allSent")}
            </div>
          )}
          {summary.missingPhone > 0 && (
            <div style={{ fontSize: 13, color: "#8c8c8c", marginTop: 8 }}>
              {t("eventRegistrations.sendInvites.missingPhone", { count: summary.missingPhone })}
            </div>
          )}
        </>
      )}
    </div>
  );

  const confirmScreen = (
    <div style={{ textAlign: "center", padding: "12px 0" }}>
      <WhatsAppOutlined style={{ fontSize: 40, color: "#25D366", marginBottom: 12 }} />
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
        {t("eventRegistrations.sendInvites.confirmTitle", { count: targetIds.length })}
      </h3>
      <div style={{ color: "#595959" }}>{t("eventRegistrations.sendInvites.confirmText")}</div>
    </div>
  );

  const sendingScreen = (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <Progress
        type="circle"
        percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0}
        strokeColor={primaryColor}
      />
      <div style={{ marginTop: 16 }}>
        {t("eventRegistrations.sendInvites.sending", { done: progress.done, total: progress.total })}
      </div>
    </div>
  );

  const doneScreen = (
    <div>
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <CheckCircleFilled style={{ fontSize: 36, color: result.failed === 0 ? "#52c41a" : "#faad14" }} />
        <div style={{ marginTop: 8, fontWeight: 600 }}>
          {t("eventRegistrations.sendInvites.doneSummary", { success: result.success, failed: result.failed })}
        </div>
      </div>
      {result.failures.length > 0 && (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>{t("eventRegistrations.sendInvites.failuresHeading")}</div>
          <div style={{ maxHeight: 220, overflowY: "auto", fontSize: 13, color: "#cf1322" }}>
            {result.failures.slice(0, 30).map((line, i) => (
              <div key={i} style={{ padding: "4px 0", borderBottom: "1px solid #f5f5f5" }}>
                {line}
              </div>
            ))}
            {result.failures.length > 30 && <div>… +{result.failures.length - 30}</div>}
          </div>
        </div>
      )}
    </div>
  );

  let footer = null;
  if (step === "choose") {
    const canContinue =
      !!summary && !loading && (choice === "pick" || (hasWhatsAppSupport && targetIds.length > 0));
    footer = [
      <Button key="cancel" onClick={handleClose}>
        {t("eventRegistrations.sendInvites.cancel")}
      </Button>,
      <Button
        key="continue"
        type="primary"
        disabled={!canContinue}
        onClick={handleContinue}
        style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
      >
        {t("eventRegistrations.sendInvites.continue")}
      </Button>,
    ];
  } else if (step === "confirm") {
    footer = [
      <Button key="back" onClick={() => setStep("choose")}>
        {t("eventRegistrations.sendInvites.back")}
      </Button>,
      <Button
        key="send"
        type="primary"
        icon={<WhatsAppOutlined />}
        onClick={runSend}
        style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
      >
        {t("eventRegistrations.sendInvites.send")}
      </Button>,
    ];
  } else if (step === "done") {
    footer = [
      <Button key="close" type="primary" onClick={handleClose} style={{ backgroundColor: primaryColor, borderColor: primaryColor }}>
        {t("eventRegistrations.sendInvites.close")}
      </Button>,
    ];
  }

  return (
    <Modal
      title={t("eventRegistrations.sendInvites.title")}
      open={open}
      onCancel={step === "sending" ? undefined : handleClose}
      closable={step !== "sending"}
      maskClosable={false}
      footer={footer}
      width={isMobile ? "calc(100vw - 16px)" : 480}
      style={isMobile ? { top: 24 } : undefined}
      destroyOnClose
    >
      {step === "choose" && chooseScreen}
      {step === "confirm" && confirmScreen}
      {step === "sending" && sendingScreen}
      {step === "done" && doneScreen}
    </Modal>
  );
};

export default SendInvitationsModal;
